import { Tooltip, Typography, Tag, Space } from 'antd';
import type { DailySchedulePlan, ScheduleJobInput, VehicleChainPlan } from '@ubus/shared';
import { isPastSchedulePickup, minutesToTimeString } from '@ubus/shared';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

function minutesLabel(minutes: number): string {
  return minutesToTimeString(minutes).slice(0, 5);
}

function snapHourFloor(minutes: number): number {
  return Math.floor(minutes / 60) * 60;
}

function snapHourCeil(minutes: number): number {
  return Math.ceil(minutes / 60) * 60;
}

function emptyVehiclePlan(
  vehicleId: string,
  fleetNumber?: string
): VehicleChainPlan {
  return {
    vehicleId,
    fleetNumber,
    legs: [],
    validation: {
      ok: true,
      errors: [],
      issues: [],
      timeline: [],
      totalDeadheadKm: 0,
    },
  };
}

export interface VehicleScheduleTimelineProps {
  plan: DailySchedulePlan;
  jobsById: Map<string, ScheduleJobInput>;
  /** YYYY-MM-DD schedule day — used to flag past pickup times. */
  scheduleDate: string;
  /** Vehicle IDs to emphasize (e.g. multi-bus published splits) */
  emphasizeVehicleIds?: Set<string>;
  /** When set, clicking a vehicle row assigns this unassigned job. */
  assignModeJobId?: string | null;
  assignModeJobTitle?: string;
  onAssignVehicle?: (vehicleId: string) => void;
  /** Highlight bars / rows for this job (assignment list linkage). */
  highlightJobId?: string | null;
  onSelectJob?: (jobId: string) => void;
  /** Brief error flash on a vehicle after failed assign. */
  flashVehicleId?: string | null;
}

/**
 * Compact day Gantt for Daily Schedule — one row per vehicle.
 * Color is not the only encoding: published bars use dashed outline + label.
 */
export function VehicleScheduleTimeline({
  plan,
  jobsById,
  scheduleDate,
  emphasizeVehicleIds,
  assignModeJobId,
  assignModeJobTitle,
  onAssignVehicle,
  highlightJobId,
  onSelectJob,
  flashVehicleId,
}: VehicleScheduleTimelineProps) {
  const { t } = useTranslation();
  const assignMode = Boolean(assignModeJobId);

  const vehicleRows: VehicleChainPlan[] = (() => {
    const byId = new Map(plan.vehicles.map((v) => [v.vehicleId, v]));
    const fleet = plan.fleet ?? [];
    if (fleet.length === 0) return plan.vehicles;
    return fleet.map(
      (f) => byId.get(f.id) ?? emptyVehiclePlan(f.id, f.fleetNumber)
    );
  })();

  const entries = vehicleRows.flatMap((v) =>
    v.validation.timeline.map((entry) => ({
      ...entry,
      vehicleId: v.vehicleId,
    }))
  );

  const hasTimedLegs = entries.length > 0;
  const rawMin = hasTimedLegs ? Math.min(...entries.map((e) => e.arriveMinutes)) : 8 * 60;
  const rawMax = hasTimedLegs ? Math.max(...entries.map((e) => e.departMinutes)) : 18 * 60;
  const rangeStart = Math.max(0, snapHourFloor(rawMin) - 60);
  const rangeEnd = Math.min(24 * 60, snapHourCeil(rawMax) + 60);
  const span = Math.max(rangeEnd - rangeStart, 60);

  const hours: number[] = [];
  for (let h = rangeStart; h <= rangeEnd; h += 60) {
    hours.push(h);
  }

  if (vehicleRows.length === 0) {
    return <Text type="secondary">{t('vehicleTimeline.noTimedLegs')}</Text>;
  }

  return (
    <div
      className={`admin-timeline${assignMode ? ' admin-timeline--assign' : ''}`}
      role="img"
      aria-label={t('vehicleTimeline.occupancy', {
        start: minutesLabel(rangeStart),
        end: minutesLabel(rangeEnd),
      })}
    >
      {assignMode && (
        <Text type="warning" className="admin-timeline__assign-hint" role="status">
          {t('vehicleTimeline.assignHint', {
            job: assignModeJobTitle ?? t('vehicleTimeline.job'),
          })}
        </Text>
      )}

      <div className="admin-timeline__legend" aria-hidden>
        <span className="admin-timeline__swatch admin-timeline__swatch--draft" />
        <Text type="secondary">{t('vehicleTimeline.draft')}</Text>
        <span className="admin-timeline__swatch admin-timeline__swatch--published" />
        <Text type="secondary">{t('vehicleTimeline.published')}</Text>
        <span className="admin-timeline__swatch admin-timeline__swatch--expired" />
        <Text type="secondary">{t('vehicleTimeline.expired')}</Text>
      </div>

      <div className="admin-timeline__scroll">
        <div
          className="admin-timeline__grid"
          style={{ minWidth: Math.max(640, hours.length * 72), width: '100%' }}
        >
          <div className="admin-timeline__axis" aria-hidden>
            <div className="admin-timeline__label-col" />
            <div className="admin-timeline__track-col">
              {hours.map((h) => (
                <span
                  key={h}
                  className="admin-timeline__tick"
                  style={{ left: `${((h - rangeStart) / span) * 100}%` }}
                >
                  {minutesLabel(h)}
                </span>
              ))}
            </div>
          </div>

          {vehicleRows.map((v) => {
            const fleet = v.fleetNumber ?? t('vehicleTimeline.unnumbered');
            const emphasized = emphasizeVehicleIds?.has(v.vehicleId);
            const flashing = flashVehicleId === v.vehicleId;
            const rowEntries = v.validation.timeline.map((entry) => ({
              ...entry,
              isFixed: v.legs.some((l) => l.legId === entry.legId && l.isFixed),
              title:
                jobsById.get(entry.jobId)?.title ??
                plan.jobTitles?.[entry.jobId] ??
                entry.label ??
                t('vehicleTimeline.job'),
            }));

            return (
              <div
                key={v.vehicleId}
                className={`admin-timeline__row${emphasized ? ' admin-timeline__row--warn' : ''}${
                  !v.validation.ok ? ' admin-timeline__row--error' : ''
                }${assignMode ? ' admin-timeline__row--pickable' : ''}${
                  flashing ? ' admin-timeline__row--flash' : ''
                }`}
                role={assignMode ? 'button' : undefined}
                tabIndex={assignMode ? 0 : undefined}
                onClick={
                  assignMode
                    ? () => onAssignVehicle?.(v.vehicleId)
                    : undefined
                }
                onKeyDown={
                  assignMode
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onAssignVehicle?.(v.vehicleId);
                        }
                      }
                    : undefined
                }
                aria-label={
                  assignMode
                    ? t('vehicleTimeline.assignTo', { fleet })
                    : undefined
                }
              >
                <div className="admin-timeline__label-col">
                  <Space size={4} wrap>
                    <Text strong>{fleet}</Text>
                    <Tag color={v.validation.ok ? 'success' : 'error'}>
                      {v.validation.ok ? t('vehicleTimeline.ok') : t('vehicleTimeline.issue')}
                    </Tag>
                  </Space>
                </div>
                <div
                  className="admin-timeline__track-col"
                  role="list"
                  aria-label={t('vehicleTimeline.schedule', { fleet })}
                >
                  {hours.map((h) => (
                    <span
                      key={`${v.vehicleId}-g-${h}`}
                      className="admin-timeline__guide"
                      style={{ left: `${((h - rangeStart) / span) * 100}%` }}
                      aria-hidden
                    />
                  ))}
                  {rowEntries.map((e) => {
                    const left = ((e.arriveMinutes - rangeStart) / span) * 100;
                    const width = Math.max(
                      ((e.departMinutes - e.arriveMinutes) / span) * 100,
                      0.8
                    );
                    const expired = isPastSchedulePickup(scheduleDate, e.arriveMinutes);
                    const label = `${e.title}: ${minutesLabel(e.arriveMinutes)}–${minutesLabel(e.departMinutes)}${
                      e.isFixed ? t('vehicleTimeline.publishedSuffix') : ''
                    }${expired ? t('vehicleTimeline.expiredSuffix') : ''}`;
                    const highlighted = highlightJobId === e.jobId;
                    return (
                      <Tooltip key={`${v.vehicleId}-${e.legId}`} title={label}>
                        <button
                          type="button"
                          role="listitem"
                          className={`admin-timeline__bar${
                            e.isFixed
                              ? ' admin-timeline__bar--published'
                              : ' admin-timeline__bar--draft'
                          }${expired ? ' admin-timeline__bar--expired' : ''}${
                            highlighted ? ' admin-timeline__bar--highlight' : ''
                          }`}
                          style={{ left: `${left}%`, width: `${width}%` }}
                          aria-label={label}
                          onClick={(ev) => {
                            if (assignMode) return;
                            ev.stopPropagation();
                            if (!e.isFixed) onSelectJob?.(e.jobId);
                          }}
                        >
                          <span className="admin-timeline__bar-text">
                            {expired ? `${t('vehicleTimeline.expired')} · ${e.title}` : e.title}
                          </span>
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Text type="secondary" className="admin-timeline__hint">
        {assignMode ? t('vehicleTimeline.assignModeHint') : t('vehicleTimeline.hint')}
      </Text>
    </div>
  );
}

export default VehicleScheduleTimeline;
